import type { FunctionComponent } from 'react';
import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { intl } from '@trello/i18n';
import { PrivateIcon } from '@trello/nachos/icons/private';
import { PublicIcon } from '@trello/nachos/icons/public';
import { token } from '@trello/theme';
import { useLazyComponent } from '@trello/use-lazy-component';

import styles from './MemberHomePrivacyLabel.less';

// The string values correspond to the i18n keys
export type PrivacyOption = 'always-hidden' | 'always-public';
export type MemberFieldName = 'bio' | 'unknown' | 'username';

const tooltips = new Map<MemberFieldName, string>([
  [
    'bio',
    intl.formatMessage({
      id: 'templates.member_home_privacy_label.tooltip-bio',
      defaultMessage:
        'Visible to anyone on the internet, including those that find you through search engines like Google. Edit your bio to reflect how you’d like people to see it.',
      description: 'Tooltip content for bio field',
    }),
  ],
  [
    'username',
    intl.formatMessage({
      id: 'templates.member_home_privacy_label.tooltip-username',
      defaultMessage:
        'Visible to anyone on the internet, including those that find you through search engines like Google. Edit your username to reflect how you’d like people to see it.',
      description: 'Tooltip content for username field',
    }),
  ],
]);

interface PrivacyIconProps {
  privacy: PrivacyOption;
}

const PrivacyOptionIcon: FunctionComponent<PrivacyIconProps> = ({
  privacy,
}) => {
  switch (privacy) {
    case 'always-public':
      return (
        <PublicIcon
          size="small"
          dangerous_className={styles.icon}
          color={token('color.icon.subtle', '#626F86')}
        />
      );
    case 'always-hidden':
      return (
        <PrivateIcon
          size="small"
          dangerous_className={styles.icon}
          color={token('color.icon.subtle', '#626F86')}
        />
      );
    default:
      throw new Error('No Icon mapping defined');
  }
};

interface PrivacyLabelProps {
  privacy: PrivacyOption;
  field?: MemberFieldName;
  className?: string;
}

export const MemberHomePrivacyLabel: FunctionComponent<PrivacyLabelProps> = ({
  className,
  privacy,
  field,
}) => {
  const MemberHomePrivacyTooltip = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "member-home-privacy-tooltip" */ './MemberHomePrivacyTooltip'
      ),
    {
      namedImport: 'MemberHomePrivacyTooltip',
      preload: false,
    },
  );

  const content = field ? tooltips.get(field) : undefined;

  const privacyText =
    privacy === 'always-hidden' ? (
      <FormattedMessage
        id="templates.member_home_privacy_label.always-hidden"
        defaultMessage="Always hidden"
        description="Always hidden"
      />
    ) : (
      <FormattedMessage
        id="templates.member_home_privacy_label.always-public"
        defaultMessage="Always public"
        description="Always public"
      />
    );

  return (
    <Suspense fallback={null}>
      <MemberHomePrivacyTooltip content={content}>
        <div className={classNames(styles.privacyLabel, className)}>
          <PrivacyOptionIcon privacy={privacy} />
          <span className={styles.labelText}>{privacyText}</span>
        </div>
      </MemberHomePrivacyTooltip>
    </Suspense>
  );
};
